'use client';

import { ReactNode } from 'react';

import { Styles, css, cva } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';

const vipButton = cva({
  base: center.raw({
    pos: 'relative',
    h: 54,
    gap: 4,
    px: 24,
    rounded: 1000,
    textStyle: 'Title/XS/Monospace',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    _disabled: {
      cursor: 'not-allowed',
      bg: 'background.default.level-1',
      border: 'none',
      color: 'content.default.level-1',
      boxShadow: 'none',
      _before: { animation: 'none', opacity: 0 },
      _after: { bg: 'background.default.level-1' },
    },
    _focusVisible: {
      outline: '2px solid',
      outlineOffset: 0,
      outlineColor: 'border.default.level-4',
    },
    '& > span': {
      zIndex: 10,
    },
  }),
  variants: {
    hierarchy: {
      primary: {
        color: 'inverse.black',
        bg: 'rgba(255, 208, 132)',
        boxShadow: '0 0 24px rgba(255, 235, 186, 0.4)',
        _before: {
          content: '""',
          pos: 'absolute',
          zIndex: 0,
          left: '50%',
          top: '50%',
          marginLeft: '-50%',
          marginTop: '-50%',
          w: '100%',
          aspectRatio: '1 / 1',
          bg: 'rgba(255, 208, 132)',
          bgImage: 'linear-gradient(rgba(255, 255, 255) 0%, rgba(255, 208, 132) 100%)',
          bgRepeat: 'no-repeat',
          animation: 'buttonBorderRotate 2s linear infinite',
        },
        _after: {
          content: '""',
          pos: 'absolute',
          zIndex: 5,
          left: 2,
          top: 2,
          w: 'calc(100% - 4px)',
          h: 'calc(100% - 4px)',
          bg: 'rgba(255, 208, 132)',
          rounded: 1000,
        },
        _hover: {
          _after: {
            bg: 'linear-gradient(rgba(0, 0, 0, 0.20)), linear-gradient(rgba(255, 208, 132))',
          },
        },
      },
      secondary: {
        color: 'rgba(255, 208, 132)',
        bg: 'rgba(255, 208, 132, 0.15)',
        border: '2px solid rgba(255, 208, 132, 0.4)',
        backdropFilter: 'blur(40px)',
        _hover: {
          bg: 'rgba(255, 208, 132, 0.4)',
        },
      },
    },
  },
  defaultVariants: {
    hierarchy: 'primary',
  },
});

interface Props {
  hierarchy?: 'primary' | 'secondary';
  label: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  css?: Styles;
}

export const VipButton = ({ hierarchy = 'primary', label, onClick, disabled = false, css: cssProps }: Props) => {
  return (
    <button type='button' onClick={onClick} disabled={disabled} className={css(vipButton.raw({ hierarchy }), cssProps)}>
      <span>{label}</span>
    </button>
  );
};
